import { useCallback } from 'react';
import type { QueryOptions } from '@apollo/client';
import { updateQuery } from '@frameio/graphql-client';
import type {
  Membership,
  Resource,
  RestoreMembershipPayload,
} from '@frameio/graphql-client/generated';
import {
  GetDirectMemberships,
  useRestoreMembershipMutation,
} from '@frameio/graphql-client/generated';
import { intl } from '@frameio/i18n';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { toSupportedLocaleOrDefault } from '@frameio/shared/utils/toSupportedLocale';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';
import { useToastTryCatchHandler } from '@frameio/utils/hooks/useToastTryCatchHandler';
import TEXT from './generated/TEXT/useRestoreMembership';

TEXT.DEFINE({
  /** Success toast message when member is restored to resource */
  successUndo: (memberName: string, resourceName: string) =>
    `${memberName} was re-added to ${resourceName}`,

  /** Error toast message when restore fails */
  errorUndo: (resourceName: string, errorMessage?: string) =>
    `Unable to re-add to ${resourceName}${
      errorMessage ? `: ${errorMessage}` : ''
    }`,
});

type Props = {
  onBeginMutation?: (id: Membership['id']) => Promise<void> | void;
  onCompleteMutation?: (id: Membership['id']) => void;
  resourceId: Resource['id'];
  resourceName: Resource['name'];
  membershipIndex: number;
};

export const useRestoreMembership = ({
  onBeginMutation,
  onCompleteMutation,
  resourceId,
  resourceName,
  membershipIndex,
}: Props) => {
  const tryCatchOnSubmit = useToastTryCatchHandler({ duration: 4000 });
  const locale = toSupportedLocaleOrDefault(intl().locale);
  const accountId = useActiveAccountId();
  const [restoreMembershipMutation] = useRestoreMembershipMutation({
    update: (cache, { data }) => {
      if (
        accountId &&
        data?.restoreMembership &&
        'membership' in data?.restoreMembership
      ) {
        const { membership } = data?.restoreMembership;
        updateQuery(
          cache,
          GetDirectMemberships,
          {
            accountId,
            locale,
            resourceId,
            page: {
              first: DEFAULT_INITIAL_PAGE_SIZE,
            },
          },
          (draft: any) => {
            const resource = draft?.account?.resource;
            if (resource) {
              resource.directMemberships.nodes = [
                ...resource.directMemberships.nodes.slice(0, membershipIndex),
                membership,
                ...resource.directMemberships.nodes.slice(membershipIndex),
              ];

              resource.directMemberships.totalCount += 1;
              resource.unfilteredDirectMembershipsTotalCount.totalCount += 1;
            }
          }
        );
      }
    },
  });

  const restoreMembership = useCallback(
    async (
      membershipId: Membership['id'],
      refetchQueries?: Array<string | QueryOptions>
    ) => {
      await onBeginMutation?.(membershipId);
      const response = await new Promise((resolve) => {
        const update = restoreMembershipMutation({
          refetchQueries,
          variables: {
            locale,
            input: {
              id: membershipId,
            },
          },
        });

        tryCatchOnSubmit({
          attempt: () => update,
          successMessage(success) {
            onCompleteMutation?.(membershipId);

            const membership = success.data.restoreMembership.membership;

            resolve(membership);

            return TEXT.successUndo(
              membership.accountUser?.name ||
                membership.accountUser?.inviteEmailAddress ||
                membership.accountUserGroup?.name,
              resourceName
            ) as string;
          },
          errorMessage(error) {
            resolve(error);

            return TEXT.errorUndo(resourceName, error) as string;
          },
        });

        return update;
      });

      return response as Promise<RestoreMembershipPayload>;
    },
    [
      onBeginMutation,
      restoreMembershipMutation,
      locale,
      tryCatchOnSubmit,
      onCompleteMutation,
      resourceName,
    ]
  );

  return restoreMembership;
};
